import { useRef } from 'react'
import Editor, { Monaco } from '@monaco-editor/react'
import type { editor } from 'monaco-editor'
import { registerDSLLanguage, getLanguageId } from '@quantquack/monaco-dsl'

interface MonacoEditorProps {
  value: string
  onChange: (value: string | undefined) => void
  language?: string
  height?: string
  readOnly?: boolean
  onMount?: (editor: editor.IStandaloneCodeEditor, monaco: Monaco) => void
  disableMinimap?: boolean
}

let languageRegistered = false

export default function MonacoEditor({
  value,
  onChange,
  language = 'quantquack-dsl',
  height = '500px',
  readOnly = false,
  onMount,
  disableMinimap = false,
}: MonacoEditorProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)

  const handleEditorDidMount = (
    editor: editor.IStandaloneCodeEditor,
    monaco: Monaco
  ) => {
    editorRef.current = editor

    // Register DSL language on first mount
    if (!languageRegistered) {
      registerDSLLanguage(monaco)
      languageRegistered = true
    }

    // Configure editor options
    editor.updateOptions({
      minimap: { enabled: true },
      fontSize: 14,
      lineNumbers: 'on',
      scrollBeyondLastLine: false,
      automaticLayout: true,
      tabSize: 2,
      wordWrap: 'on',
      suggestOnTriggerCharacters: true,
      quickSuggestions: {
        other: true,
        comments: false,
        strings: false,
      },
      acceptSuggestionOnCommitCharacter: true,
      acceptSuggestionOnEnter: 'on',
      snippetSuggestions: 'top',
    })

    // Call custom onMount if provided
    if (onMount) {
      onMount(editor, monaco)
    }
  }

  // Use DSL language if not specified
  const editorLanguage = language === 'quantquack-dsl' || language === 'javascript' 
    ? getLanguageId() 
    : language

  return (
    <Editor
      height={height}
      language={editorLanguage}
      value={value}
      onChange={onChange}
      onMount={handleEditorDidMount}
      theme="vs-dark"
      options={{
        readOnly,
        minimap: { enabled: disableMinimap ? false : true },
        fontSize: 14,
        lineNumbers: 'on',
        scrollBeyondLastLine: false,
        automaticLayout: true,
        tabSize: 2,
        wordWrap: 'on',
        formatOnPaste: true,
        formatOnType: true,
        suggestOnTriggerCharacters: true,
        quickSuggestions: {
          other: true,
          comments: false,
          strings: false,
        },
        acceptSuggestionOnCommitCharacter: true,
        acceptSuggestionOnEnter: 'on',
        snippetSuggestions: 'top',
      }}
    />
  )
}
